import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronRight, Eye, Calendar, Tag, X } from 'lucide-react';
import Layout from '@/components/Layout';
import { NewsCard, NoticeItem } from '@/components/Cards';
import { NEWS_LIST, NOTICES, type NewsItem } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';

type CategoryTab = '全部' | '综合新闻' | '学术活动' | '通知公告' | '媒体南开';

const ITEMS_PER_PAGE = 6;

const categoryColors: Record<string, string> = {
  '综合新闻': 'bg-primary/10 text-primary',
  '学术活动': 'bg-chart-1/20 text-chart-1',
  '通知公告': 'bg-destructive/10 text-destructive',
  '媒体南开': 'bg-chart-4/20 text-chart-4',
};

const featuredImages = [
  IMAGES.CAMPUS_1,
  IMAGES.RESEARCH_5,
  IMAGES.LIBRARY_3,
  IMAGES.GRADUATION_2,
];

const imageMap: Record<string, string> = {
  GRADUATION_2: IMAGES.GRADUATION_2,
  RESEARCH_1: IMAGES.RESEARCH_1,
  CAMPUS_3: IMAGES.CAMPUS_3,
  CAMPUS_7: IMAGES.CAMPUS_7,
  LIBRARY_1: IMAGES.LIBRARY_1,
  RESEARCH_4: IMAGES.RESEARCH_4,
  GRADUATION_4: IMAGES.GRADUATION_4,
  RESEARCH_3: IMAGES.RESEARCH_3,
};

function resolveImage(key: string): string {
  return imageMap[key] ?? IMAGES.CAMPUS_1;
}

export default function News() {
  const [activeTab, setActiveTab] = useState<CategoryTab>('全部');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const featuredNews = NEWS_LIST[0];

  const filteredNews = useMemo(() => {
    let list = NEWS_LIST;
    if (activeTab !== '全部') {
      list = list.filter((n) => n.category === activeTab);
    }
    if (selectedCategory) {
      list = list.filter((n) => n.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.excerpt.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeTab, selectedCategory, searchQuery]);

  const totalPages = Math.ceil(filteredNews.length / ITEMS_PER_PAGE);
  const paginatedNews = filteredNews.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value as CategoryTab);
    setCurrentPage(1);
    setSelectedCategory(null);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (cat: string) => {
    setSelectedCategory((prev) => (prev === cat ? null : cat));
    setCurrentPage(1);
    setActiveTab('全部');
  };

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    NEWS_LIST.forEach((n) => {
      counts[n.category] = (counts[n.category] ?? 0) + 1;
    });
    return counts;
  }, []);

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative h-72 md:h-96 overflow-hidden">
          <img
            src={resolveImage(featuredNews.image)}
            alt={featuredNews.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-sidebar/70 via-sidebar/50 to-sidebar/80" />
          <div className="relative z-10 h-full flex flex-col justify-end pb-10 px-4">
            <div className="container mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 35 }}
              >
                <Badge className="mb-3 bg-chart-1/20 text-chart-1 border-chart-1/30">
                  {featuredNews.category}
                </Badge>
                <h1
                  className="text-2xl md:text-4xl font-bold text-white mb-2"
                  style={{ fontFamily: "'Noto Serif SC', serif" }}
                >
                  {featuredNews.title}
                </h1>
                <p className="text-white/80 text-sm md:text-base max-w-2xl line-clamp-2">
                  {featuredNews.excerpt}
                </p>
                <div className="flex items-center gap-4 mt-3 text-white/70 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {featuredNews.date}
                  </span>
                  {featuredNews.views && (
                    <span className="flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" />
                      {featuredNews.views.toLocaleString()} 阅读
                    </span>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="bg-sidebar py-3">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 text-sm text-sidebar-foreground/70">
              <span>首页</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-sidebar-primary">新闻资讯</span>
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="搜索新闻标题或内容..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="pl-9 border-border bg-card"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
                  <TabsList className="bg-muted border border-border h-auto flex-wrap gap-1 p-1">
                    {(['全部', '综合新闻', '学术活动', '通知公告', '媒体南开'] as CategoryTab[]).map((tab) => (
                      <TabsTrigger
                        key={tab}
                        value={tab}
                        className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm px-4"
                      >
                        {tab}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>

                {searchQuery && (
                  <div className="mb-4 flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      搜索 &ldquo;{searchQuery}&rdquo; 找到 {filteredNews.length} 条结果
                    </span>
                  </div>
                )}

                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${activeTab}-${currentPage}-${searchQuery}-${selectedCategory}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 35 }}
                  >
                    {paginatedNews.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {paginatedNews.map((item: NewsItem, idx) => {
                          const resolved: NewsItem = {
                            ...item,
                            image: resolveImage(item.image),
                          };
                          return (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                type: 'spring',
                                stiffness: 300,
                                damping: 35,
                                delay: idx * 0.05,
                              }}
                            >
                              <NewsCard item={resolved} />
                            </motion.div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-20 text-center">
                        <div className="text-muted-foreground text-6xl mb-4">📰</div>
                        <p className="text-muted-foreground text-lg">暂无相关新闻</p>
                        <p className="text-muted-foreground text-sm mt-1">请尝试其他关键词或分类</p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="border-border"
                    >
                      上一页
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 ${currentPage === page ? 'bg-primary text-primary-foreground' : 'border-border'}`}
                      >
                        {page}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="border-border"
                    >
                      下一页
                    </Button>
                  </div>
                )}
              </div>

              <aside className="w-full lg:w-72 flex-shrink-0 space-y-6">
                <div className="bg-card border border-border rounded-lg p-5">
                  <h3
                    className="text-base font-semibold text-foreground mb-4 flex items-center gap-2"
                    style={{ fontFamily: "'Noto Serif SC', serif" }}
                  >
                    <Tag className="w-4 h-4 text-primary" />
                    按分类筛选
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(categoryColors).map(([cat, colorClass]) => (
                      <button
                        key={cat}
                        onClick={() => handleCategoryFilter(cat)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-all ${
                          selectedCategory === cat
                            ? 'bg-primary/10 border border-primary/30'
                            : 'hover:bg-muted border border-transparent'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
                            {cat}
                          </span>
                        </span>
                        <span className="text-muted-foreground text-xs font-mono">
                          {categoryCounts[cat] ?? 0}
                        </span>
                      </button>
                    ))}
                    {selectedCategory && (
                      <button
                        onClick={() => setSelectedCategory(null)}
                        className="w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-2 py-1.5 rounded border border-dashed border-border hover:border-ring transition-colors"
                      >
                        <X className="w-3 h-3" />
                        清除筛选
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-5">
                  <h3
                    className="text-base font-semibold text-foreground mb-4 flex items-center gap-2"
                    style={{ fontFamily: "'Noto Serif SC', serif" }}
                  >
                    <Calendar className="w-4 h-4 text-primary" />
                    最新通知
                  </h3>
                  <div className="space-y-1">
                    {NOTICES.map((notice, idx) => (
                      <div key={notice.id}>
                        <NoticeItem notice={notice} />
                        {idx < NOTICES.length - 1 && <Separator className="my-1" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-5">
                  <h3
                    className="text-base font-semibold text-foreground mb-4"
                    style={{ fontFamily: "'Noto Serif SC', serif" }}
                  >
                    热门图片
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {featuredImages.map((src, idx) => (
                      <div
                        key={idx}
                        className="aspect-square rounded overflow-hidden group cursor-pointer"
                      >
                        <img
                          src={src}
                          alt={`校园图片 ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
